// Handbag clearance LP — cart handshake (module; the picker is a classic script and calls window.LHCCart.add).
// ONE /cart/add.js with every line of the set (the handbag + its free wallets, a wallet picked twice as one line with
// quantity 2; each carries `_ltr_lp` = the key the Shopify Function gates on, a visible `Set`, and the pre-order
// properties when the variant is armed), then /cart.js → CartLinesUpdateEvent so Horizon's drawer opens.
// No cart attribute is written: the Function gates on the LINE property only.
//
// Resolves { status }:
//   'ok'       lines added, drawer opened, the picked set totals expectCents per set
//   'price'    lines added but the cart does not carry the set price → the just-added units are REMOVED again
//              (/cart/change.js) so nothing mispriced is left behind; the caller shows the price error
//   'degraded' lines ARE in the cart but a later step (cart.js / event) failed → the caller must not ask to add again
// Rejects { soldOut: true, message } on a 422 from /cart/add.js (nothing was added) or { stage: 'add' } on any other
// failure of the add itself (nothing was added). Failures after the add never reject.
import { CartLinesUpdateEvent } from '@shopify/events';

async function cartPost(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = {};
    try { detail = await res.json(); } catch (_e) { /* non-JSON error body */ }
    const err = new Error(`${path} ${res.status}`);
    err.soldOut = res.status === 422;
    err.message = detail.description || err.message;
    throw err;
  }
  return res.json();
}

/** true when the gated units of the picked variants total expectCents per complete set;
 *  sets = min over the requested lines of (units of that variant in the cart ÷ units one set needs). */
export function setPriceHolds(cart, key, items, expectCents) {
  const ids = items.map((l) => l.id);
  const mine = (cart.items || []).filter((it) => it.properties && it.properties._ltr_lp === key && ids.includes(it.variant_id));
  if (!Number.isFinite(expectCents) || !mine.length) return false;
  const inCart = (id) => mine.filter((it) => it.variant_id === id).reduce((q, it) => q + it.quantity, 0);
  const sets = Math.min(...items.map((l) => Math.floor(inCart(l.id) / l.quantity)));
  const paid = mine.reduce((sum, it) => sum + it.final_line_price, 0);
  return sets > 0 && paid === expectCents * sets;
}

async function fetchCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`/cart.js ${res.status}`);
  return res.json();
}

function announce(items, ajaxCart, key) {
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: 'lettoria-handbag-clearance', sourceId: key },
      }),
    })
  );
}

/** Takes back exactly the units this tap added: each line goes to (quantity after the add − units requested). */
async function removeJustAdded(added, items) {
  for (const it of added) {
    const requested = items.filter((l) => l.id === it.variant_id).reduce((q, l) => q + l.quantity, 0) || 1;
    await cartPost('/cart/change.js', { id: it.key, quantity: Math.max(0, it.quantity - requested) });
  }
}

async function add({ items, key, expectCents }) {
  let added;
  try {
    const res = await cartPost(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', { items });
    added = res.items || [];
  } catch (e) {
    throw e.soldOut ? { soldOut: true, message: e.message } : { stage: 'add' };
  }
  // from here on the lines are in the cart: nothing below may reject
  try {
    const ajaxCart = await fetchCart();
    if (setPriceHolds(ajaxCart, key, items, expectCents)) {
      announce(items, ajaxCart, key);
      return { status: 'ok' };
    }
    await removeJustAdded(added, items);
    announce([], await fetchCart(), key); // refresh the drawer without the removed lines
    return { status: 'price' };
  } catch (e) {
    console.error('clearance add: post-add step failed', e);
    return { status: 'degraded' };
  }
}

window.LHCCart = { add };
